import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { SceneItem } from '../types/scene';
import { LayerContainer } from './types';
import { TransitionConfig } from '../TransitionConfig';
import { AnimationHelpers } from '../helpers';

/**
 * Internal component to handle scene rendering with transitions and freeze frames
 */
const SceneRenderer: React.FC<{
    scenes: SceneItem[];
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ scenes, fps, theme, aspectRatio, defaults, videoConfig: passedVideoConfig }) => {
    const currentFrame = useCurrentFrame();
    const remotionVideoConfig = useVideoConfig();
    const currentTime = currentFrame / fps;

    // Use passed videoConfig if available, otherwise fall back to Remotion context
    const videoConfig = passedVideoConfig || { width: remotionVideoConfig.width, height: remotionVideoConfig.height };

    // Debug logging (dev only)
    if (process.env.NODE_ENV !== 'production') {
        console.debug('[SceneRenderer] COMPONENT EXECUTING at frame', currentFrame);
        if (currentFrame === 0) {
            console.debug('[SceneRenderer] Frame 0 Debug:', {
                totalScenes: scenes.length,
                passedVideoConfig,
                remotionVideoConfig: { width: remotionVideoConfig.width, height: remotionVideoConfig.height, fps: remotionVideoConfig.fps },
                usingVideoConfig: videoConfig,
                firstSceneSrc: scenes[0]?.inputs?.[0]?.src
            });
        }
    }

    // Find active scenes and potential transitions
    const activeScenes = scenes.filter(scene => {
        // Use the same timing calculation as individual scenes with default fallback
        const effectiveTransitionIn = scene.overrideTransitions?.in || defaults?.transitionIn || { type: 'fade', duration: 0.5 };
        const effectiveTransitionOut = scene.overrideTransitions?.out || defaults?.transitionOut || { type: 'fade', duration: 0.5 };

        const { startFrame, durationFrames } = AnimationHelpers.calculateSceneTransitionTiming(
            scene.startAt,
            scene.duration,
            fps,
            effectiveTransitionIn,
            effectiveTransitionOut
        );

        const extendedStart = startFrame / fps;
        const extendedEnd = (startFrame + durationFrames) / fps;

        // FIX: Use inclusive end boundary to keep previous scene visible while next loads
        // At boundary frames, both scenes render (older underneath, newer on top via z-index)
        // This prevents black flash when Scene 2's video isn't ready yet at frame boundary
        return currentTime >= extendedStart && currentTime <= extendedEnd;
    });

    // Debug - Check scene activity at key frames (dev only)
    if (process.env.NODE_ENV !== 'production' && (currentFrame === 0 || currentFrame === 100 || currentFrame === 500)) {
        console.debug(`[SceneRenderer] Frame ${currentFrame} - Active scenes: ${activeScenes.length}/${scenes.length}`, {
            currentTime,
            activeSceneIds: activeScenes.map(s => s.id),
            firstSceneDetails: scenes[0] ? {
                id: scenes[0].id,
                startAt: scenes[0].startAt,
                duration: scenes[0].duration,
                inputs: scenes[0].inputs.length
            } : null
        });
    }

    // Sort active scenes by start time in ASCENDING order (later scenes render on top)
    // This ensures newer scenes render over older ones during boundary frame overlaps
    const sortedScenes = [...activeScenes].sort((a, b) => a.startAt - b.startAt);

    return (
        <>
            {sortedScenes.map(scene => (
                <React.Fragment key={scene.id}>
                    {scene.render(fps, theme, aspectRatio, defaults, videoConfig)}
                </React.Fragment>
            ))}
        </>
    );
};


export class SceneLayerContainer implements LayerContainer {
    items: SceneItem[];

    constructor(items: SceneItem[]) {
        this.items = items;
    }

    addScene(scene: SceneItem): void {
        this.items.push(scene);
    }

    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        return (
            <AbsoluteFill>
                <SceneRenderer
                    scenes={this.items}
                    fps={fps}
                    theme={theme}
                    aspectRatio={aspectRatio}
                    defaults={defaults}
                    videoConfig={videoConfig}
                />
            </AbsoluteFill>
        );
    }
}

/**
 * React component wrapper for SceneLayerContainer
 * This allows the container to be used directly as a JSX component
 */
export const SceneLayerContainerComponent: React.FC<{
    items: SceneItem[];
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
}> = ({ items, fps, theme, aspectRatio, defaults }) => {
    const container = new SceneLayerContainer(items);
    return <>{container.render(fps, theme, aspectRatio, defaults)}</>;
}; 